import { useRef, useEffect, useCallback } from 'react';

export function useNumberScrollAnimation(
    toNumber: number,
    options: {
        duration?: number;
        once?: boolean; // true = 只执行一次
    } = {},
) {
    const { duration = 1500, once = true } = options;

    const ref = useRef<HTMLDivElement | null>(null);
    const playedRef = useRef(false);
    const frameRef = useRef<number | null>(null);

    const animate = useCallback(() => {
        if (!ref.current) return;
        if (frameRef.current) cancelAnimationFrame(frameRef.current);

        const el = ref.current;
        const start = 0;
        const startTime = performance.now();

        const update = (now: number) => {
            const progress = Math.min((now - startTime) / duration, 1);
            const current = Math.floor(start + (toNumber - start) * progress);

            el.innerText = current.toLocaleString();

            if (progress < 1) {
                frameRef.current = requestAnimationFrame(update);
            }
        };

        frameRef.current = requestAnimationFrame(update);
    }, [duration, toNumber]);

    // 可视区检测
    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting) {
                    if (once && playedRef.current) return; // 只执行一次
                    playedRef.current = true;
                    animate();
                }
            },
            {
                threshold: 0.3, // 元素 30% 出现时触发
            },
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [animate, once]);

    return ref;
}
